import React, { useEffect, useState, useCallback } from "react";
import { Drawer, Input, Select, DatePicker, Button, Tabs, Badge, Pagination } from "antd";
import { PlusOutlined, SearchOutlined, CustomerServiceOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { getTickets } from "../../services/supportService";
import TicketList from "./TicketList";
import TicketDetail from "./TicketDetail";
import CreateTicketForm from "./CreateTicketForm";

const { Option } = Select;
const { RangePicker } = DatePicker;

const CATEGORIES = [
  { value: "", label: "All Categories" },
  { value: "BOOKING", label: "Booking" },
  { value: "POS", label: "POS" },
  { value: "ACCOUNTS", label: "Accounts" },
  { value: "CHANNEL_MANAGER", label: "Channel Manager" },
  { value: "BOOKING_ENGINE", label: "Booking Engine" },
  { value: "PAYMENT_GATEWAY", label: "Payment Gateway" },
  { value: "OTHERS", label: "Others" },
];

const TAB_STATUSES = {
  open: ["OPEN", "IN_PROGRESS"],
  closed: ["RESOLVED", "CLOSED"],
};

const PAGE_SIZE = 10;

export default function SupportPanel({ open, onClose }) {
  const hotelId = useSelector((s) => s.login?.hotelDetails?.id);

  const [view, setView] = useState("list");
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [activeTab, setActiveTab] = useState("open");
  const [tickets, setTickets] = useState([]);
  const [totalTickets, setTotalTickets] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [dateRange, setDateRange] = useState([]);

  const fetchTickets = useCallback(async (page = 0) => {
    if (!hotelId) return;
    setLoading(true);
    try {
      console.log(TAB_STATUSES[activeTab])
      const params = {
        statuses: TAB_STATUSES[activeTab]?.join(','),
        page,
        size: PAGE_SIZE,
      };
      if (category) params.category = category;
      if (search) params.searchText = search;
      if (dateRange?.[0]) params.fromDate = dateRange[0].format("YYYY-MM-DD");
      if (dateRange?.[1]) params.toDate = dateRange[1].format("YYYY-MM-DD");

      const res = await getTickets(hotelId, params);
      // Backend returns Spring Page: { content: [], totalElements, ... }
      setTickets(res.data?.content || []);
      setTotalTickets(res.data?.totalElements || 0);
      setCurrentPage(page);
    } catch {
      setTickets([]);
      setTotalTickets(0);
    } finally {
      setLoading(false);
    }
  }, [hotelId, activeTab, category, search, dateRange]);

  useEffect(() => {
    if (open && view === "list") fetchTickets(0);
  }, [open, view, fetchTickets]);

  // Reset to page 0 when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(0);
  };

  const handleSelectTicket = (id) => {
    setSelectedTicketId(id);
    setView("detail");
  };

  const handleCreated = () => {
    setView("list");
    fetchTickets(0);
  };

  const renderListView = () => (
    <div className="flex flex-col h-full">
      {/* Filters */}
      <div className="p-3 space-y-2 border-b border-gray-200 bg-gray-50">
        <Input
          placeholder="Search tickets..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={() => fetchTickets(0)}
          size="small"
          prefix={<SearchOutlined />}
          allowClear
          onClear={() => fetchTickets(0)}
        />
        <div className="flex gap-2">
          <Select
            size="small"
            value={category}
            onChange={(v) => setCategory(v)}
            className="flex-1"
          >
            {CATEGORIES.map((c) => (
              <Option key={c.value} value={c.value}>{c.label}</Option>
            ))}
          </Select>
          <Button size="small" onClick={() => fetchTickets(0)}>Filter</Button>
        </div>
        <RangePicker
          size="small"
          className="w-full"
          onChange={(dates) => setDateRange(dates || [])}
        />
      </div>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        size="small"
        className="px-3 flex-shrink-0"
        items={[
          {
            key: "open",
            label: <span>Open &amp; In Progress</span>,
          },
          {
            key: "closed",
            label: <span>Closed</span>,
          },
        ]}
      />

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        <TicketList tickets={tickets} loading={loading} onSelect={handleSelectTicket} />
      </div>

      {/* Pagination */}
      {totalTickets > PAGE_SIZE && (
        <div className="px-3 py-2 border-t border-gray-100 flex justify-center">
          <Pagination
            current={currentPage + 1}
            pageSize={PAGE_SIZE}
            total={totalTickets}
            size="small"
            onChange={(p) => fetchTickets(p - 1)}
            showSizeChanger={false}
          />
        </div>
      )}

      {/* Create button */}
      <div className="p-3 border-t border-gray-200">
        <Button
          type="primary"
          icon={<PlusOutlined />}
          block
          onClick={() => setView("create")}
        >
          New Ticket
        </Button>
      </div>
    </div>
  );

  return (
    <Drawer
      title={
        <div className="flex items-center gap-2">
          <CustomerServiceOutlined className="text-blue-500" />
          <span>Support</span>
          {totalTickets > 0 && view === "list" && (
            <Badge count={totalTickets} size="small" />
          )}
        </div>
      }
      placement="right"
      width={400}
      open={open}
      onClose={() => {
        onClose();
        setView("list");
        setSelectedTicketId(null);
      }}
      bodyStyle={{ padding: 0, display: "flex", flexDirection: "column" }}
    >
      <div className="flex flex-col h-full overflow-hidden">
        {view === "list" && renderListView()}
        {view === "create" && (
          <CreateTicketForm
            hotelId={hotelId}
            onBack={() => setView("list")}
            onCreated={handleCreated}
          />
        )}
        {view === "detail" && (
          <TicketDetail
            ticketId={selectedTicketId}
            onBack={() => {
              setView("list");
              fetchTickets(currentPage);
            }}
          />
        )}
      </div>
    </Drawer>
  );
}
